import { ElementRef, forwardRef, useMemo } from 'react';
import { css } from '@emotion/react';
import { motion } from 'motion/react';
import { theme } from '@frontend/theme';
import type { CalendarEntityType } from '../../../stores/calendar.store';
import { useCalendarView, useCalendarViewProps } from '../providers';
import { ColumnEntity, LocationWithCalendarColumnEntities } from '../types';
import { checkIsNonAttendedEventStatus, zIndex } from '../utils';
import { AllDayOutEventsBar } from './all-day-out-events-bar';
import { LocationListWithColumnEntities } from './location-list-with-column-entities';
import { ProviderScrollWrapper } from './scrollable-buttons';

interface Props {
  calendarEntityType?: CalendarEntityType;
  containerRef: React.RefObject<ElementRef<'article'>>;
  gridTemplateColumns: string;
}

export const CalendarColumnEntityListSection = forwardRef<HTMLElement, Props>(
  ({ calendarEntityType = 'provider', containerRef, gridTemplateColumns }, ref) => {
    const { data = [] } = useCalendarViewProps();
    const { containerState } = useCalendarView(['containerState']);
    const maxWidth = containerState.clientWidth ?? 0;

    const list = useMemo<LocationWithCalendarColumnEntities[]>(() => {
      const mapped = data.map((location) => ({
        id: location.locationId,
        name: location.officeName,
        timezone: location.officeTimezone,
        columnEntities: location.columnEntities
          ? location.columnEntities.map((columnEntity) => {
              // If both firstName and lastName are empty, split name field
              // NOTE: For now, we have added this splitting for V2 data support
              let firstName = columnEntity.firstName;
              let lastName = columnEntity.lastName;

              if (!firstName && !lastName && columnEntity.entityName) {
                const nameParts = columnEntity.entityName.trim().split(' ');
                firstName = nameParts[0] || '';
                lastName = nameParts.slice(1).join(' ') || '';
              }

              return {
                id: columnEntity.entityId,
                firstName,
                lastName,
                avatarSrc: columnEntity.src,
                isArchived: columnEntity.archived,
                iconName: columnEntity.iconName,
                iconColor: columnEntity.iconColor,
                eventCount:
                  columnEntity.events.filter(
                    (event) => event.type !== 'unavailable' && !checkIsNonAttendedEventStatus(event.status)
                  ).length ?? 0,
                avatarVariant: 'circle',
              } satisfies ColumnEntity;
            })
          : [],
      }));
      // Include empty locations in header only when other locations have providers,
      // so that location bars appear above the per-location no-providers banners.
      const hasAnyProviders = mapped.some((loc) => !!loc.columnEntities?.length);
      return mapped.filter((location) => hasAnyProviders || !!location.columnEntities?.length);
    }, [data]);

    return (
      <motion.section ref={ref} css={headerContainerStyle}>
        <ProviderScrollWrapper containerRef={containerRef}>
          <LocationListWithColumnEntities calendarEntityType={calendarEntityType} list={list} maxWidth={maxWidth} />
        </ProviderScrollWrapper>
        <AllDayOutEventsBar gridTemplateColumns={gridTemplateColumns} />
      </motion.section>
    );
  }
);

CalendarColumnEntityListSection.displayName = 'CalendarColumnEntityListSection';

const headerContainerStyle = css({
  position: 'sticky',
  top: 0,
  display: 'flex',
  flexDirection: 'column',
  boxShadow: theme.shadows.floating,
  background: theme.colors.white,
  zIndex: zIndex.providerSection,
  transition: 'height 0.3s ease',
});
